/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerHome
 * -------------
 * The organizer landing page — the "Pulse" layout: capacity ring, registration
 * and collection tiles, a 26-week registration velocity strip, the organizer's
 * weekly streak, top route loops and what is coming up.
 *
 * Data: reads `summary` if given, otherwise falls back to SAMPLE_SUMMARY and
 * shows a "Sample data" chip so nobody mistakes placeholder figures for live
 * ones. When the events API lands, pass the real object in and the chip goes.
 */

import React from 'react';
import { retrieve } from '../../utils/storage_helper';
import SAMPLE_SUMMARY from './sampleSummary';
import '../../styles/organizer.css';

const RING_R = 51;
const RING_C = 2 * Math.PI * RING_R;

function OrganizerHome({ summary }) {
  const data = summary || SAMPLE_SUMMARY;
  const userData = retrieve('userData');
  const firstName = userData?.first_name || data.organizerName || 'there';

  const { flagship, registrations, collections, velocityWeeks, streakWeeks, routes, upcoming } = data;
  const fillPct = flagship.capacity
    ? Math.round((flagship.registered / flagship.capacity) * 100)
    : 0;
  const dash = (Math.min(fillPct, 100) / 100) * RING_C;

  const peakWeek = Math.max.apply(null, velocityWeeks);
  const peakSparkline = Math.max.apply(null, registrations.lastSevenDays);

  return (
    <>
      <header className="org-topbar">
        <div>
          <h1>Evening, {firstName}</h1>
          <p className="org-sub">
            {flagship.name} flags off in {flagship.daysToGo} days ·{' '}
            {(flagship.capacity - flagship.registered).toLocaleString('en-IN')} slots left
          </p>
        </div>
        {data.sample && (
          <span className="org-sample" title="Placeholder figures — the events API is not wired up yet">
            Sample data
          </span>
        )}
        <button type="button" className="org-btn" style={{ marginLeft: 'auto' }}>
          + New event
        </button>
      </header>

      <div className="org-content">
        <div className="org-bento">

          {/* capacity ring */}
          <section className="org-card org-sp2">
            <p className="org-lab">Capacity · {flagship.name}</p>
            <div className="org-ring">
              <svg
                width="124" height="124" viewBox="0 0 124 124" role="img"
                aria-label={`${fillPct}% of ${flagship.capacity} slots filled`}
              >
                <circle cx="62" cy="62" r={RING_R} fill="none" stroke="var(--org-surface-hi)" strokeWidth="15" />
                <circle
                  cx="62" cy="62" r={RING_R} fill="none" stroke="var(--org-accent)" strokeWidth="15"
                  strokeLinecap="round" strokeDasharray={`${dash} ${RING_C}`}
                  transform="rotate(-90 62 62)"
                />
                <text
                  x="62" y="59" textAnchor="middle" fill="var(--org-text)"
                  fontFamily="Archivo, Poppins, sans-serif" fontWeight="800" fontSize="28" letterSpacing="-1.2"
                >{fillPct}%</text>
                <text
                  x="62" y="77" textAnchor="middle" fill="var(--org-mute)"
                  fontFamily="Martian Mono, monospace" fontSize="8.5"
                >{flagship.registered} / {flagship.capacity}</text>
              </svg>
              <dl style={{ flex: 1, minWidth: 152, margin: 0 }}>
                {flagship.categories.map((c) => (
                  <div className="org-kv" key={c.label}>
                    <span>{c.label}{c.full ? ' · full' : ''}</span>
                    <b className="org-num" style={c.full ? { color: 'var(--org-accent)' } : undefined}>
                      {c.count.toLocaleString('en-IN')}
                    </b>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          {/* registrations */}
          <section className="org-card">
            <p className="org-lab">Registrations</p>
            <div className="org-metric org-big org-num">{registrations.total.toLocaleString('en-IN')}</div>
            <span className="org-delta">▲ {registrations.deltaWeek} / 7d</span>
            <div className="org-mini" aria-hidden="true">
              {registrations.lastSevenDays.map((v, i) => (
                <i
                  key={i}
                  className={v === peakSparkline ? 'hot' : undefined}
                  style={{ height: `${Math.round((v / peakSparkline) * 100)}%` }}
                />
              ))}
            </div>
            <p className="org-lab" style={{ marginTop: 9 }}>Peak day · {peakSparkline} sign-ups</p>
          </section>

          {/* collections */}
          <section className="org-card">
            <p className="org-lab">Collections</p>
            <div className="org-metric org-big org-num">{collections.net}</div>
            <span className="org-delta">{collections.deltaLabel}</span>
            <div className="org-kv" style={{ marginTop: 15, borderTop: '1px solid var(--org-line)', paddingTop: 11 }}>
              <span>Pending settlement</span><b className="org-num">{collections.pending}</b>
            </div>
            <div className="org-kv">
              <span>Refund requests</span>
              <b className="org-num" style={{ color: 'var(--org-rec)' }}>{collections.refunds}</b>
            </div>
          </section>

          {/* velocity heat strip */}
          <section className="org-card org-sp3">
            <p className="org-lab">Registration velocity · {velocityWeeks.length} weeks</p>
            <div className="org-heat" aria-hidden="true">
              {velocityWeeks.map((w, i) => (
                <i key={i} style={{ opacity: (0.1 + 0.9 * Math.pow(w / peakWeek, 0.72)).toFixed(2) }} />
              ))}
            </div>
            <div className="org-legend">
              <span>Quiet</span>
              {[0.12, 0.3, 0.52, 0.76, 1].map((o) => <i key={o} style={{ opacity: o }} />)}
              <span>Peak week · {peakWeek} sign-ups</span>
            </div>
          </section>

          {/* streak */}
          <section className="org-card org-streak">
            <p className="org-lab">Organizer streak</p>
            <div className="org-metric org-big org-num" style={{ fontSize: 34 }}>
              {streakWeeks}
              <span style={{ fontSize: 15, letterSpacing: 0, fontWeight: 500 }}> wks</span>
            </div>
            <p className="org-lab">An event live every week</p>
          </section>

          {/* routes */}
          <section className="org-card org-sp2">
            <p className="org-lab">Most-run route loops</p>
            <ul className="org-list">
              {routes.map((r) => (
                <li key={r.name}>
                  <span className="org-rk">{r.rank}</span>
                  <span>{r.name}<span className="org-rm">{r.meta}</span></span>
                  <span className="org-rv">{r.runs.toLocaleString('en-IN')}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* upcoming */}
          <section className="org-card org-sp2">
            <p className="org-lab">Next up</p>
            <ul className="org-list">
              {upcoming.map((e) => (
                <li key={e.name}>
                  <span className="org-rk">{e.day}</span>
                  <span>{e.name}<span className="org-rm">{e.meta}</span></span>
                  <span className={`org-pill ${e.status}`} style={{ marginLeft: 'auto' }}>{e.statusLabel}</span>
                </li>
              ))}
            </ul>
          </section>

        </div>
      </div>
    </>
  );
}

export default OrganizerHome;
